import classNames from "classnames";
import { useContext, useEffect, useState } from "react";
import { zoneMapping } from "../../data";
import { getDistrictsCommitteeList } from "../../http/chapterApi";
import { cmsUrl } from "../../http/cmsApi";
import { Chapter, DistrictMember, Person } from "../../types/apiTypes";
import { Contact, Frontmatter } from "../../types/types";
import { DataContext } from "../AppRouter";
import Box from "../Box";
import PhoneLink from "../PhoneLink";
import styles from "../Boxes.module.css";
import PersonBoxes from "../PersonBoxes";
import Loading from "../ui/Loading";

interface Props {
  frontmatter: Frontmatter;
}

const ContactBoxes = ({ frontmatter }: Props) => {
  const [persons, setPersons] = useState<Person[]>();
  const [loading, setLoading] = useState(true);
  const { chapter } = useContext(DataContext);

  useEffect(() => {
    const fetchData = async () => {
      const fetchedCommittee =
        chapter && (await getDistrictsCommitteeList(chapter.area.district.id));

      const sortedCommittee = fetchedCommittee?.sort((a, b) => {
        const aNum = a.isPresident
          ? 1
          : a.isDirector
            ? 2
            : a.isDistrictChairman
              ? 3
              : a.isAreaChairman
                ? 4
                : 5;
        const bNum = b.isPresident
          ? 1
          : b.isDirector
            ? 2
            : b.isDistrictChairman
              ? 3
              : b.isAreaChairman
                ? 4
                : 5;

        return aNum - bNum;
      });

      const persons = sortedCommittee && getTypedPersons(sortedCommittee);

      setPersons(persons ?? []);
      setTimeout(() => setLoading(false), 350);
    };

    setLoading(true);
    fetchData();
  }, [chapter]);

  return !persons || loading ? (
    <Loading />
  ) : (
    <>
      <div>
        {frontmatter.functionary_title && (
          <h2 className={styles["contact-subtitle"]}>
            {frontmatter.functionary_title}
          </h2>
        )}
        <PersonBoxes items={persons} showImage={true} />
      </div>
      <Employees
        title={frontmatter.association_secretary_title}
        items={filterByDistrict(
          frontmatter.association_secretary_items,
          chapter,
        )}
      />
      <Employees
        title={frontmatter.member_supervisor_title}
        items={filterByZoneIds(frontmatter.member_supervisor_items, chapter)}
      />
    </>
  );
};

const getTypedPersons = (persons: DistrictMember[]): Person[] =>
  persons.map((item) => ({
    id: String(item.id),
    picture: item.person.picture,
    name: item.person.firstName,
    lastName: item.person.lastName,
    email: item.person.email,
    mobile: item.person.mobile,
    street: item.person.street,
    zip: item.person.zip,
    city: item.person.city,
    companyName: item.company,
    roles: item.roles,
  }));

const getZones = (zoneIds: string[]): string[] => {
  const zones = Object.fromEntries(
    zoneMapping.map(({ id, name }) => [id, name]),
  );

  return zoneIds
    .map((zoneId) => zones[zoneId])
    .filter((name) => name !== undefined);
};

const buildZoneString = (zoneIds: string[]): string => {
  const zones = getZones(zoneIds);

  if (zones.length === 1) return `HGV Zone ${zones[0]}`;

  const allExceptLast = zones.slice(0, -1).join(", ");
  const last = zones[zones.length - 1];

  return `HGV Zonen ${allExceptLast} und ${last}`;
};

const zoneIdsByChapterId = Object.fromEntries(
  zoneMapping.reduce<[number, number][]>(
    (carry, zone) => [
      ...carry,
      ...zone.chapterIds.map((id) => [id, zone.id] as [number, number]),
    ],
    [],
  ),
);

const filterByDistrict = (
  items: Contact[],
  chapter: Chapter | undefined,
): Contact[] =>
  chapter
    ? items
        .filter(
          (item) =>
            !item.district ||
            item.district
              .toLowerCase()
              .includes(chapter.area.district.name.toLowerCase()),
        )
        .sort((a, b) => (a.position > b.position ? -1 : 1))
    : [];

const filterByZoneIds = (
  items: Contact[],
  chapter: Chapter | undefined,
): Contact[] =>
  chapter
    ? items
        .filter(({ zones }) => {
          const zoneId = zoneIdsByChapterId[chapter.id];
          return zoneId === undefined ? false : zones?.includes(String(zoneId));
        })
        .sort((a, b) => (a.position > b.position ? -1 : 1))
    : [];

interface EmployeeProps {
  title: string;
  items: Contact[];
}

const Employees = ({ title, items }: EmployeeProps) => {
  return (
    <>
      <div className={styles["boxes-separator-container"]}>
        <hr className={styles["boxes-separator"]} />
      </div>
      <div>
        {title && <h2 className={styles["contact-subtitle"]}>{title}</h2>}
        <div className={classNames(styles.boxes, styles["contact-boxes"])}>
          {items.map((item, index) => (
            <Box
              img={item.photo}
              placeholder={true}
              url={`${cmsUrl}${location.pathname}`}
              key={index}
            >
              <div className={styles["content-fit"]}>
                <h3 className={styles.name}>{item.name}</h3>
                <p className={styles.text}>{item.position}</p>
                <p className={styles.text}>
                  {!item.zones?.length
                    ? "HGV - Hoteliers- und Gastwirteverband"
                    : buildZoneString(item.zones)}
                </p>
                <p className={styles.text}>
                  <a href={`mailto:${item.email}`} className={styles.email}>
                    {item.email}
                  </a>
                </p>
                {item.office.map((office) => (
                  <div className={styles["office-container"]} key={office.name}>
                    <p className={styles.text}>
                      <strong>{office.name}</strong>
                    </p>
                    <p className={styles.text}>{office.address}</p>
                    <p className={styles.text}>
                      Tel. <PhoneLink number={office.phone} />
                    </p>
                    {office.fax && (
                      <p className={styles.text}>Fax. +39 {office.fax}</p>
                    )}
                  </div>
                ))}
              </div>
            </Box>
          ))}
        </div>
      </div>
    </>
  );
};

export default ContactBoxes;
